/* eslint-disable import/no-unresolved */
import { isNil, sortBy } from 'lodash';
import uuid from 'react-uuid';
import { Allocation } from 'api';
import { DATE_ALIAS } from 'common/constants/allocations';
import * as FOC from 'common/constants/fund-ownership';
import { CellValue, ICell } from 'models/interfaces/spreadsheet-types/core-types';
import disableSecuritySelectionCell from 'pages/CapTable/fund-ownership/components/shares-ledger/utilities/disableSecuritySelectionCell';
import { onCellsChanged } from 'utilities';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import { shortDate } from 'utilities/dateFormats';
import { FundOwnershipTableData } from '../models/fund-ownership.models';
import { FundOwnershipCell, FundOwnershipConfigurationRow, ReverseParsedFundOwnershipDetail } from '../types';
import { FundOwnershipTableAlias } from './rowConfig';

const fundOwnershipFields = [FOC.SHARES_LEDGER_ALIAS, FOC.CASH_DISTRIBUTION_LEDGER_ALIAS];

const allocationFields = [DATE_ALIAS];

const parser = async ({
  columns,
  rowConfig,
  tableData,
}: {
  columns: ReverseParsedFundOwnershipDetail[];
  rowConfig: FundOwnershipConfigurationRow[];
  tableData: FundOwnershipTableData;
}) => {
  const { securities } = tableData?.capTable || {};
  const sortedColumns = sortBy(columns, ['order']);
  const alphabet = alphabetGenerator([], columns.length);
  const changes: any[] = [];

  let colIndex = 0;
  let cells: Record<string, Partial<FundOwnershipCell>> = {};
  let columnSecurity: string | null = '';

  const treatValues = (alias: string, value: CellValue) => {
    if (alias === 'investment_date') {
      return shortDate(value as string);
    }
    if (['date', 'title'].includes(alias)) {
      return value;
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'object') {
      return value;
    }
    if (typeof value === 'boolean') {
      return Number(value);
    }
    if (isNil(value)) {
      return 0;
    }
    if (!Number.isNaN(value)) {
      return Number(value);
    }
    if (Number.isNaN(value)) {
      return value;
    }
    return value;
  };

  const replacePlaceholders = (columnLegend: string, id: string, str = '') => {
    const totalSharesOutstanding = tableData?.calculations?.[id]?.totalSharesOutstanding ?? 0;
    const cumulativeDividends = tableData?.calculations?.[id]?.cumulativeDividends ?? 0;
    const multipleOfFactor = tableData?.calculations?.[id]?.multipleOfFactor ?? 1;

    const newExpression = str
      .replace(/{{columnLegend}}/g, columnLegend)
      .replace(/{{columnSecurity}}/g, columnSecurity ?? '')
      .replace(/{{totalSharesOutstanding}}/g, `${totalSharesOutstanding}`)
      .replace(/{{cumulativeDividends}}/g, `${cumulativeDividends}`)
      .replace(/{{multipleOfFactor}}/g, `${multipleOfFactor}`);
    return newExpression;
  };

  const getLinkedCells = (linkedCells: string[], columnLegend: string, columnLegends: string[]): Set<string> =>
    new Set(
      linkedCells.flatMap(linkedCell => {
        if (linkedCell.includes('@@')) {
          return columnLegends.map(legend => linkedCell.replace('@@', legend));
        }
        return linkedCell.replace('@', columnLegend);
      })
    );

  // Add content columns, iterating through each sorted column
  sortedColumns.forEach(foColumn => {
    const columnLegend = alphabet[colIndex];
    const columnRef = foColumn.column_ref || foColumn.columnRef || uuid();
    let rowIndex = 1;

    // Get the selected column security
    const selectedSecurity = foColumn.security ?? '';

    const matchedSecurity = securities?.find(security => security.id?.toString() === selectedSecurity?.toString());

    columnSecurity = matchedSecurity ? matchedSecurity.name : null;

    // Add properties from the sorted columns using the order of the row config
    rowConfig.forEach(
      ({ alias, expr, dataSourceKey, defaultValue, format, className, linkedCellSymbols, readOnly }) => {
        const key = columnLegend + rowIndex;
        const exprAux = replacePlaceholders(columnLegend, `${foColumn.id}`, expr ?? '');
        const customSecurityName = foColumn.custom_security_name ?? '';
        const isCustomSecurityColumn = foColumn.is_custom_security;
        const isCustomSecurityCell = alias === FOC.CUSTOM_SECURITY_NAME_ALIAS && isCustomSecurityColumn;
        const currentValue = isCustomSecurityCell ? customSecurityName : foColumn[alias as keyof typeof foColumn];
        const value = treatValues(alias, currentValue);

        const foCell: Partial<FundOwnershipCell> = {
          className,
          value,
          alias,
          key,
          format,
          expr: exprAux,
          dataSourceKey: replacePlaceholders(columnLegend, dataSourceKey ?? ''),
          columnId: foColumn.id,
          columnRef,
          columnLegend,
          defaultValue,
          columnOrder: foColumn.order,
          linkedCellSymbols: getLinkedCells(linkedCellSymbols || [], columnLegend, alphabet),
          acquisitions: foColumn.acquisitions || [],
        };

        if (alias === FundOwnershipTableAlias.FUND_ENTITY) {
          foCell.value = foColumn.fund_entity;
        }

        if (alias === FOC.SECURITY_ALIAS && isCustomSecurityColumn) {
          foCell.value = customSecurityName;
        } else if (
          alias === FOC.SECURITY_ALIAS &&
          foColumn?.acquisitions?.length &&
          !readOnly &&
          !isCustomSecurityColumn
        ) {
          disableSecuritySelectionCell(foCell as ICell);
        }

        cells = {
          ...cells,
          [key]: foCell,
        };

        if ([FOC.FULLY_DILUTED_OWNERSHIP_ROW_NUMBER, FOC.LIQUIDATION_PREFERENCE_PLUS_ROW_NUMBER].includes(rowIndex)) {
          changes.push({
            cell: {
              ...cells[key],
            },
            value: exprAux,
          });
        }

        rowIndex += 1;
      }
    );

    // Add Cap Table hidden fields
    const getCells = (fields: string[], getValue: (alias: string) => CellValue) => {
      fields.forEach(alias => {
        const key = `${alias}_${columnLegend}`;
        const value = getValue(alias);

        cells = {
          ...cells,
          [key]: {
            key,
            value,
            alias,
            columnLegend,
          },
        };
      });
    };

    // Add fund ownership hidden properties
    const getFundOwnershipValue = (alias: string) => foColumn[alias as keyof typeof foColumn] ?? null;
    getCells(fundOwnershipFields, getFundOwnershipValue);

    // Add allocation hidden fields
    const getAllocationValue = (alias: string) =>
      treatValues(alias, tableData.allocation?.[alias as keyof typeof Allocation]);
    getCells(allocationFields, getAllocationValue);

    colIndex += 1;
  });

  // Compute expressions
  cells = await onCellsChanged(changes, cells);
  return cells;
};

export default parser;
